import { useEffect, useState } from 'react';

export type DeviceKind = 'phone' | 'tablet' | 'desktop';
export type DeviceOrientation = 'portrait' | 'landscape';
export type DeviceDim = 'D0' | 'D1' | 'D2' | 'D3' | 'D4';

export type DeviceClassState = {
  device: DeviceKind;
  orientation: DeviceOrientation;
  dim: DeviceDim;
};

function readFinePointer(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(pointer: fine)').matches;
}

/** Pure classifier for tests — mirrors useDeviceClass rules. */
export function computeDeviceClass(
  innerWidth: number,
  innerHeight: number,
  finePointer = readFinePointer(),
): DeviceClassState {
  const shortestSide = Math.min(innerWidth, innerHeight);
  const orientation: DeviceOrientation =
    innerWidth >= innerHeight ? 'landscape' : 'portrait';

  let device: DeviceKind;
  if (shortestSide < 600) {
    device = 'phone';
  } else if (shortestSide < 1024) {
    device = 'tablet';
  } else if (finePointer) {
    device = 'desktop';
  } else {
    device = 'desktop';
  }

  let dim: DeviceDim;
  if (device === 'desktop') {
    dim = 'D0';
  } else if (device === 'phone' && orientation === 'portrait') {
    dim = 'D1';
  } else if (device === 'phone' && orientation === 'landscape') {
    dim = 'D2';
  } else if (device === 'tablet' && orientation === 'portrait') {
    dim = 'D3';
  } else {
    dim = 'D4';
  }

  return { device, orientation, dim };
}

export function readDeviceClass(): DeviceClassState {
  if (typeof window === 'undefined') {
    return { device: 'desktop', orientation: 'landscape', dim: 'D0' };
  }
  return computeDeviceClass(window.innerWidth, window.innerHeight, readFinePointer());
}

/** Compact shell: phone + tablet dims (bottom sheets, compact chrome). */
export function isCompactDim(dim: DeviceDim): boolean {
  return dim !== 'D0';
}

/** Short phone landscape pill chrome (D2). */
export function isLandscapeShortDim(dim: DeviceDim): boolean {
  return dim === 'D2';
}

/**
 * Device dimension hook (D0–D4) for layout + CSS `[data-dim]` selectors.
 */
export function useDeviceClass(): DeviceClassState {
  const [state, setState] = useState(readDeviceClass);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const sync = () => setState(readDeviceClass());
    sync();

    window.addEventListener('resize', sync);
    const portraitMq = window.matchMedia('(orientation: portrait)');
    const landscapeMq = window.matchMedia('(orientation: landscape)');
    const fineMq = window.matchMedia('(pointer: fine)');
    portraitMq.addEventListener('change', sync);
    landscapeMq.addEventListener('change', sync);
    fineMq.addEventListener('change', sync);

    return () => {
      window.removeEventListener('resize', sync);
      portraitMq.removeEventListener('change', sync);
      landscapeMq.removeEventListener('change', sync);
      fineMq.removeEventListener('change', sync);
    };
  }, []);

  return state;
}
